export interface BillHeader {
  supplierName?: string;
  invoiceNumber?: string;
  invoiceDate?: string;
  dueDate?: string;
  accountNumber?: string;
  currentBillAmount?: number;
  totalAmountDue?: number;
  billingPeriod?: string;
  address?: string;
  city?: string;
  state?: string;
  postCode?: string;
  country?: string;
  currency?: string;
  templateId?: string;
  templateName?: string;
  templateVersion?: string;
  fileName?: string;
  fileExtension?: string;
  parsedDate?: string;
  isConsolidateBill?: boolean;
  isFinalBill?: boolean;
  isOCRBill?: boolean;
  annualUsage?: number;
  paymentStatus?: string;
  needs_review: string[];
  confidence_summary?: { avg?: number };
  [key: string]: any;
}

export interface LineItem {
  description?: string;
  quantity?: number;
  unitPrice?: number;
  total?: number;
  [key: string]: any;
}

// Best-effort transform from API response -> canonical UI structure
export function transformParsed(apiResponse: any, supplierName: string, confidenceThreshold = 0.5): { header: BillHeader; lineItems: LineItem[] } {
  const data = apiResponse?.data ?? apiResponse ?? {};
  const results = data.results ?? {};
  const lines = Array.isArray(data.lines) ? data.lines : [];
  // Normalize header fields
  const header: BillHeader = {
    supplierName: results.supplierName ?? supplierName,
    invoiceNumber: results.invoiceNumber ?? results.invoice_no ?? results.invoice ?? undefined,
    invoiceDate: results.invoiceDate ?? results.invoice_date ?? undefined,
    dueDate: results.dueDate ?? results.paymentDueDate ?? undefined,
    accountNumber: results.accountNumber ?? results.accountNo ?? undefined,
    currentBillAmount: toNumber(results.currentBillAmount ?? results.amountDue ?? results.total),
    totalAmountDue: toNumber(results.totalAmountDue ?? results.amountDue ?? results.total),
    billingPeriod: results.billingPeriod ?? results.period ?? undefined,
    address: results.address ?? results.billingAddress ?? undefined,
    city: results.city ?? undefined,
    state: results.state ?? undefined,
    postCode: results.postCode ?? results.postcode ?? results.zip ?? undefined,
    country: results.country ?? undefined,
    currency: results.currency ?? undefined,
    templateId: results.templateId ?? undefined,
    templateName: results.templateName ?? undefined,
    templateVersion: results.templateVersion ?? undefined,
    fileName: results.fileName ?? undefined,
    fileExtension: results.fileExtension ?? undefined,
    parsedDate: results.parsedDate ?? new Date().toISOString(),
    isConsolidateBill: results.isConsolidateBill ?? false,
    isFinalBill: results.isFinalBill ?? false,
    isOCRBill: results.isOCRBill ?? false,
    annualUsage: toNumber(results.annualUsage),
    paymentStatus: results.paymentStatus ?? undefined,
    needs_review: [],
    confidence_summary: { avg: clamp01(averageConfidence(data?.confidence)) },
  };

  // Mark fields below threshold as needs_review if confidence map available
  const confidence = (data?.confidence ?? {}) as Record<string, number>;
  const reviewFields: string[] = [];
  for (const [k, v] of Object.entries(confidence)) {
    const key = normalizeKey(k);
    const val = typeof v === 'number' ? v : 1;
    if (val < confidenceThreshold) {
      reviewFields.push(key);
    }
  }
  header.needs_review = reviewFields;

  // Normalize line items
  const lineItems: LineItem[] = lines.map((l: any) => ({
    description: l.description ?? l.item ?? l.detail ?? '',
    quantity: toNumber(l.quantity ?? l.qty),
    unitPrice: toNumber(l.unitPrice ?? l.price),
    total: toNumber(l.total ?? l.lineTotal ?? l.amount),
    ...l,
  }));

  return { header, lineItems };
}

function toNumber(v: any): number | undefined {
  if (v === null || v === undefined || v === '') return undefined;
  const n = typeof v === 'string' ? Number(v.replace(/[^0-9.-]/g, '')) : Number(v);
  return isNaN(n) ? undefined : n;
}

function averageConfidence(conf?: Record<string, number>): number {
  if (!conf) return 0.85;
  const vals = Object.values(conf).filter((x) => typeof x === 'number') as number[];
  if (vals.length === 0) return 0.85;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

function clamp01(n?: number): number | undefined {
  if (typeof n !== 'number') return n;
  return Math.max(0, Math.min(1, n));
}

function normalizeKey(k: string): string {
  return k.toLowerCase().replace(/\s+/g, '_');
}
